import type {
    SsoCertificate,
    SsoClientAuthMethod,
    SsoConflictCode,
    SsoEmailDomain,
    SsoEmailSource,
    SsoLoginMethod,
    SsoNameIdFormat,
    SsoProtocol,
    SsoScimEventAction,
    SsoScimToken,
} from '~/services/ssoClient.types';
import dayjs from '~/utils/dayjs';

export const DOMAIN_GRACE_PERIOD_DAYS = 7;
export const CERTIFICATE_EXPIRY_WARNING_DAYS = 30;
export const MAX_CERTIFICATES = 5;
export const MAX_NAME_ATTRIBUTES = 3;
export const MAX_SCOPES = 10;
export const MAX_NAME_CLAIMS = 3;
export const SESSION_LIFETIME_MINUTES_MIN = 15;
export const SESSION_LIFETIME_MINUTES_MAX = 480;
export const SESSION_LIFETIME_MINUTES_DEFAULT = 120;
export const SCIM_TOKEN_DAYS_DEFAULT = 180;
export const SCIM_TOKEN_EXPIRY_WARNING_DAYS = 14;

export const LOGIN_METHOD_LABELS: Record<SsoLoginMethod, string> = {
    password: 'password',
    sso: 'SSO',
    impersonation: 'impersonation',
    emergency_token: 'emergency token',
};

export const loginMethodLabel = (method: SsoLoginMethod | null | undefined): string =>
    method ? LOGIN_METHOD_LABELS[method] : 'unknown';

export type DomainStatusView = 'pending' | 'verified' | 'failing' | 'unverified';

export const domainStatusView = (domain: SsoEmailDomain): DomainStatusView => {
    if (domain.status === 'verified') {
        return domain.failing_since === null ? 'verified' : 'failing';
    }

    return domain.status;
};

export const domainStatusLabel: Record<DomainStatusView, string> = {
    pending: 'Pending verification',
    verified: 'Verified',
    failing: 'Verification failing',
    unverified: 'Unverified',
};

export const domainStatusColor: Record<DomainStatusView, 'amber' | 'success' | 'rose' | 'slate'> = {
    pending: 'amber',
    verified: 'success',
    failing: 'rose',
    unverified: 'slate',
};

export const graceDaysLeft = (domain: SsoEmailDomain, now = dayjs()): number | null => {
    if (domain.failing_since === null) {
        return null;
    }

    return Math.max(0, DOMAIN_GRACE_PERIOD_DAYS - now.diff(dayjs.utc(domain.failing_since), 'day'));
};

export const checkResultHint: Record<NonNullable<SsoEmailDomain['last_check_result']>, string> = {
    found: 'The record was found.',
    not_found: 'No TXT record was found at the host below. DNS changes can take up to an hour to show.',
    mismatch: 'A TXT record was found but its value does not match. Copy the value below exactly.',
    dns_error: 'The domain could not be looked up. Try again in a few minutes.',
};

export type CertificateState = 'valid' | 'expiring' | 'expired';

export const certificateState = (certificate: Pick<SsoCertificate, 'not_after'>, now = dayjs()): CertificateState => {
    const notAfter = dayjs.utc(certificate.not_after);

    if (notAfter.isBefore(now)) {
        return 'expired';
    }

    return notAfter.diff(now, 'day') < CERTIFICATE_EXPIRY_WARNING_DAYS ? 'expiring' : 'valid';
};

export const certificateStateClass: Record<CertificateState, string> = {
    valid: 'text-slate-500',
    expiring: 'text-amber-700',
    expired: 'text-rose-800',
};

export const SCIM_TOKEN_EXPIRY_OPTIONS: { value: number; label: string }[] = [
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
    { value: 180, label: '180 days (recommended)' },
    { value: 365, label: '1 year' },
];

export type ScimTokenState = 'active' | 'expiring' | 'expired';

export const scimTokenState = (token: Pick<SsoScimToken, 'expires_at'>, now = dayjs()): ScimTokenState => {
    const expiresAt = dayjs.utc(token.expires_at);

    if (expiresAt.isBefore(now)) {
        return 'expired';
    }

    return expiresAt.diff(now, 'day') < SCIM_TOKEN_EXPIRY_WARNING_DAYS ? 'expiring' : 'active';
};

export const scimTokenStateLabel: Record<ScimTokenState, string> = {
    active: 'Active',
    expiring: 'Expiring soon',
    expired: 'Expired',
};

export const scimTokenStateColor: Record<ScimTokenState, 'success' | 'amber' | 'rose'> = {
    active: 'success',
    expiring: 'amber',
    expired: 'rose',
};

export const revocationChannelLive = (until: string | null, now = dayjs()): boolean =>
    until !== null && dayjs.utc(until).isAfter(now);

const SCIM_EVENT_LABELS: Record<SsoScimEventAction, string> = {
    created: 'Added to the team',
    linked: 'Linked to an existing member',
    activated: 'Reactivated',
    deactivated: 'Deactivated and signed out of SSO',
    deleted: 'Deleted at the identity provider and signed out of SSO',
    refused: 'Refused',
};

export const scimEventLabel = (action: SsoScimEventAction): string => SCIM_EVENT_LABELS[action];

const SCIM_REFUSAL_LABELS: Record<string, string> = {
    duplicate: 'already provisioned',
    email_invalid: 'not an email address',
    email_lookalike: 'look-alike email',
    email_not_verified_domain: 'email outside verified domains',
    platform_admin: 'platform admin',
    suspended: 'account suspended',
    team_closed: 'team closed',
    team_owner: 'team owner',
};

export const scimRefusalLabel = (reason: string | null): string =>
    reason ? SCIM_REFUSAL_LABELS[reason] ?? reason : '';

export const PROTOCOL_OPTIONS: { value: SsoProtocol; label: string; description: string }[] = [
    { value: 'saml2', label: 'SAML 2.0', description: 'Works with most identity providers.' },
    {
        value: 'oidc',
        label: 'OpenID Connect',
        description:
            'Voxie keeps checking with your identity provider while people are logged in, so a removed user loses access within the session lifetime.',
    },
];

export const CLIENT_AUTH_METHOD_OPTIONS: { value: SsoClientAuthMethod; label: string; description: string }[] = [
    {
        value: 'client_secret_basic',
        label: 'Authorization header',
        description: 'client_secret_basic, what an Okta app uses unless changed through its API.',
    },
    {
        value: 'client_secret_post',
        label: 'Request body',
        description: 'client_secret_post, what Entra ID and Google Workspace apps use.',
    },
];

export const NAME_ID_FORMAT_OPTIONS: { value: SsoNameIdFormat; label: string }[] = [
    { value: 'persistent', label: 'Persistent (recommended)' },
    { value: 'emailAddress', label: 'Email address' },
    { value: 'unspecified', label: 'Unspecified' },
];

export interface IdpPreset {
    key: 'entra' | 'okta' | 'google' | 'adfs';
    label: string;
    name_id_format: SsoNameIdFormat;
    email_source: SsoEmailSource;
    email_attribute: string | null;
    name_attributes: string[];
}

export const IDP_PRESETS: IdpPreset[] = [
    {
        key: 'entra',
        label: 'Microsoft Entra ID',
        name_id_format: 'persistent',
        email_source: 'attribute',
        email_attribute: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
        name_attributes: [
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
        ],
    },
    {
        key: 'okta',
        label: 'Okta',
        name_id_format: 'unspecified',
        email_source: 'attribute',
        email_attribute: 'email',
        name_attributes: ['firstName', 'lastName'],
    },
    {
        key: 'google',
        label: 'Google Workspace',
        name_id_format: 'emailAddress',
        email_source: 'name_id',
        email_attribute: null,
        name_attributes: ['first_name', 'last_name'],
    },
    {
        key: 'adfs',
        label: 'AD FS',
        name_id_format: 'unspecified',
        email_source: 'attribute',
        email_attribute: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
        name_attributes: [
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
        ],
    },
];

export interface OidcPreset {
    key: 'entra' | 'okta' | 'google';
    label: string;
    issuer_placeholder: string;
    client_auth_method: SsoClientAuthMethod;
    scopes: string[];
    email_claim: string;
    name_claims: string[];
}

// Google refuses offline_access; the server asks it for a refresh token through its own parameters.
export const OIDC_PRESETS: OidcPreset[] = [
    {
        key: 'entra',
        label: 'Microsoft Entra ID',
        issuer_placeholder: 'https://login.microsoftonline.com/<tenant id>/v2.0',
        client_auth_method: 'client_secret_post',
        scopes: ['openid', 'profile', 'email', 'offline_access'],
        email_claim: 'email',
        name_claims: ['given_name', 'family_name'],
    },
    {
        key: 'okta',
        label: 'Okta',
        issuer_placeholder: 'https://<org>.okta.com',
        client_auth_method: 'client_secret_basic',
        scopes: ['openid', 'profile', 'email', 'offline_access'],
        email_claim: 'email',
        name_claims: ['given_name', 'family_name'],
    },
    {
        key: 'google',
        label: 'Google Workspace',
        issuer_placeholder: 'https://accounts.google.com',
        client_auth_method: 'client_secret_post',
        scopes: ['openid', 'profile', 'email'],
        email_claim: 'email',
        name_claims: ['given_name', 'family_name'],
    },
];

export const parseScopes = (text: string): string[] =>
    text.split(/\s+/).filter((scope, index, all) => scope !== '' && all.indexOf(scope) === index);

export interface ParsedIdpMetadata {
    idp_entity_id: string;
    idp_sso_url: string;
    certificates: string[];
}

const SAML_MD = 'urn:oasis:names:tc:SAML:2.0:metadata';
const XML_DSIG = 'http://www.w3.org/2000/09/xmldsig#';
const REDIRECT_BINDING = 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect';

const toPem = (base64: string): string => {
    const body =
        base64
            .replace(/\s+/g, '')
            .match(/.{1,64}/g)
            ?.join('\n') ?? '';

    return `-----BEGIN CERTIFICATE-----\n${body}\n-----END CERTIFICATE-----`;
};

// Fills the form only; the server validates the certificates and URLs on save.
export const parseIdpMetadata = (xml: string): ParsedIdpMetadata | null => {
    const document = new DOMParser().parseFromString(xml, 'application/xml');
    const idps = Array.from(document.getElementsByTagNameNS(SAML_MD, 'IDPSSODescriptor'));
    const idp = idps.length === 1 ? idps[0] : undefined;
    const entity = idp?.parentElement ?? null;

    if (
        idp === undefined ||
        entity === null ||
        entity.namespaceURI !== SAML_MD ||
        entity.localName !== 'EntityDescriptor'
    ) {
        return null;
    }

    const redirect = Array.from(idp.getElementsByTagNameNS(SAML_MD, 'SingleSignOnService')).find(
        (service) => service.getAttribute('Binding') === REDIRECT_BINDING
    );

    if (redirect === undefined) {
        return null;
    }

    const certificates = Array.from(idp.getElementsByTagNameNS(SAML_MD, 'KeyDescriptor'))
        .filter((key) => ['signing', null].includes(key.getAttribute('use')))
        .flatMap((key) => Array.from(key.getElementsByTagNameNS(XML_DSIG, 'X509Certificate')))
        .map((node) => toPem(node.textContent ?? ''))
        .filter((pem, index, all) => all.indexOf(pem) === index);

    return {
        idp_entity_id: entity.getAttribute('entityID') ?? '',
        idp_sso_url: redirect.getAttribute('Location') ?? '',
        certificates,
    };
};

export const conflictMessage: Record<SsoConflictCode, string> = {
    require_sso_enabled: 'Turn off Require SSO first. While it is on, the connection cannot be disabled or removed.',
    protocol_mismatch: 'Remove the connection before switching between SAML 2.0 and OpenID Connect.',
    issuer_mismatch: 'Remove the connection before pointing it at another issuer.',
    last_verified_domain: 'Turn off Require SSO first. While it is on, the last verified domain cannot be removed.',
    connection_missing: 'Connect an identity provider before turning on Require SSO.',
    connection_disabled: 'Connect and enable an identity provider before turning on Require SSO.',
    no_verified_domain: 'Verify at least one email domain before turning on Require SSO.',
};

interface ConflictResponse {
    response?: { status?: number; data?: { code?: string; message?: string } };
}

// A 409 carries {message, code}; the code picks the sentence and the server message is the fallback.
export const conflictText = (
    error: unknown,
    messages: Partial<Record<SsoConflictCode, string>> = {}
): string | null => {
    const response = (error as ConflictResponse)?.response;

    if (response?.status !== 409) {
        return null;
    }

    const code = response.data?.code;

    if (code !== undefined && code in conflictMessage) {
        return messages[code as SsoConflictCode] ?? conflictMessage[code as SsoConflictCode];
    }

    return response.data?.message ?? 'This change is not allowed right now.';
};
